/**
 * Checkout page — the frontend's implementation of `04_PRICING_ENGINE.md §4`
 * (Purchase → Pricing) and `00_PAYMENT_SYSTEM_ARCHITECTURE.md`'s "no cart"
 * flow: one Collection, priced by the backend Pricing Engine, confirmed by
 * the user, then handed to Purchase → Payment.
 *
 * This page NEVER computes a price. Every amount shown comes from
 * `usePricing` (a live backend Price Quote) or, after Buy Now, from the
 * created `Purchase.amount` (the backend's own frozen figure) — never a
 * value calculated here.
 *
 * Reached via a "Buy Now" action from `ProductDetailPage`, or directly from
 * the wizard's "Buy a Publish Credit" CTA (`StepPublish.tsx`). In the latter
 * case a buy-credit intent (`purchase/buyCreditIntent.ts`) is present: the
 * Purchase is tagged with the draft's `invitationId`, and once paid the page
 * offers "Continue to publish" back to that exact draft.
 */
import { useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/src/modules/components/Button';
import { Icons } from '@/src/modules/components/Icons';
import { ConfirmModal } from '@/src/modules/components/settings/ConfirmModal';
import { TemplateCardPreview } from '@/src/modules/renderer/TemplateCardPreview';
import { formatPrice } from '@/src/infrastructure/lib/currency';
import { useCollection } from '@/src/modules/catalog/useCollection';
import { usePricing } from '@/src/modules/pricing/usePricing';
import { PriceBreakdown } from '@/src/modules/pricing/components/PriceBreakdown';
import { CouponInput } from '@/src/modules/pricing/components/CouponInput';
import { useCreatePurchase } from '@/src/modules/purchase/useCreatePurchase';
import { PAYMENT_UNAVAILABLE_MESSAGE } from '@/src/modules/purchase/pollPurchaseOutcome';
import { clearBuyCreditIntent, peekBuyCreditIntent } from '@/src/modules/purchase/buyCreditIntent';
import { useAuthStore } from '@/src/modules/auth/useAuthStore';

// Fixed, reviewed copy — never `useCollection`'s raw `error.message` on this
// money-adjacent page (Pricing Engine architecture §12/§16: never expose
// internal backend errors). `ProductDetailPage` shows the raw message for
// this same failure; that is a pre-existing, lower-stakes precedent this
// page deliberately does not inherit — caught in code review.
const COLLECTION_LOAD_FAILED_MESSAGE = 'We couldn’t load this product. Please try again.';

export function CheckoutPage() {
  const { productId } = useParams<{ productId: string }>();
  const { data: collection, loading, error, notFound, reload } = useCollection(productId);
  const pricing = usePricing(collection?.code);
  const purchaseFlow = useCreatePurchase();
  const navigate = useNavigate();
  const isPartner = useAuthStore((s) => s.isPartner());
  // Read once per visit: validated (UUID + /wizard-only returnTo) inside
  // peekBuyCreditIntent, so `returnTo` is safe to navigate to.
  const [buyCreditIntent] = useState(peekBuyCreditIntent);

  // Set only when a fresh Purchase's backend-frozen amount differs from the
  // amount last previewed — the user must explicitly see and accept the
  // real number before payment proceeds (never silently assumed still valid).
  const [priceChangeNotice, setPriceChangeNotice] = useState<{
    previewedPaise: number;
    previewedCurrency: string;
    frozenPaise: number;
    frozenCurrency: string;
  } | null>(null);

  // `ConfirmModal` always calls `onClose` after a successful `onConfirm`
  // resolves (see its own source) — so `onClose` cannot tell "the user
  // cancelled" apart from "confirm just finished" by checking component
  // state/props, because BOTH `onConfirm` and `onClose` are closures fixed
  // to whichever CheckoutPage render was current when the modal opened, and
  // that render's `purchaseFlow` binding never mutates even after later
  // re-renders (the same stale-closure class of bug fixed elsewhere on this
  // page — see `useCreatePurchase.ts`'s note on `proceedToCheckout`). A
  // ref's `.current` mutation, by contrast, IS visible to every closure that
  // holds the ref, regardless of which render created them — so `onConfirm`
  // flips it synchronously, before doing anything async, and `onClose`
  // reads it to decide whether it's cleaning up after a real confirm or
  // handling an actual cancel. Caught in code review: the original version
  // called `purchaseFlow.reset()` unconditionally in `onClose`, which wiped
  // out a *successful* checkout-creation immediately after it completed.
  const priceChangeConfirmedRef = useRef(false);

  // Minted once, lazily, the first time it's actually read (not eagerly via
  // `useRef(crypto.randomUUID())`, which would call it on every render only
  // to discard every result but the first — caught in code review) — the
  // short-lived, single-use client request token `01_PURCHASE_DOMAIN.md
  // §11`/§19 requires whenever no `invitationId` exists yet to key
  // domain-level idempotency on (this feature's only current case). Sent as
  // the `Idempotency-Key` header on every `createPurchase` call, including a
  // same-attempt retry after a failure — re-minted only once the in-flight
  // attempt reaches a terminal state (the user declines a price-change
  // confirmation and the Purchase is cancelled), never on every render or
  // every retry, since a fresh key on a retry would defeat its own purpose.
  // `crypto.randomUUID` is unavailable in non-secure contexts (plain
  // `http://` on a non-localhost host); falls back to a timestamp+random
  // string rather than throwing during render and blanking the page.
  const idempotencyKeyRef = useRef<string | null>(null);
  function mintIdempotencyKey(): string {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `fallback-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
  function getIdempotencyKey(): string {
    if (idempotencyKeyRef.current === null) idempotencyKeyRef.current = mintIdempotencyKey();
    return idempotencyKeyRef.current;
  }

  if (notFound) {
    return (
      <Navigate to="/catalog" replace state={{ catalogBanner: 'That product is no longer available.' }} />
    );
  }

  const isBusy =
    purchaseFlow.phase === 'creating-purchase' ||
    purchaseFlow.phase === 'creating-checkout' ||
    purchaseFlow.phase === 'cancelling';
  const isReadyForPayment = purchaseFlow.phase === 'ready-for-payment';

  async function handleConfirmAndPay() {
    if (!collection) return;

    // A Purchase already exists from an earlier attempt whose checkout-order
    // creation failed — retry ONLY that step. Re-running createPurchaseStep
    // here would create a second Purchase for the same intent (caught in
    // code review).
    if (purchaseFlow.purchase && purchaseFlow.phase === 'error') {
      await purchaseFlow.proceedToCheckout(purchaseFlow.purchase);
      return;
    }

    const previewedPaise = pricing.quote?.totalPaise;
    const previewedCurrency = pricing.quote?.currency;
    const created = await purchaseFlow.createPurchaseStep({
      collectionCode: collection.code,
      couponCode: pricing.appliedCouponCode ?? undefined,
      ...(buyCreditIntent ? { invitationId: buyCreditIntent.invitationId } : {}),
      idempotencyKey: getIdempotencyKey(),
    });
    if (!created) return; // error already surfaced via purchaseFlow.error

    const priceChanged =
      previewedPaise !== undefined &&
      previewedCurrency !== undefined &&
      (created.amount.totalPaise !== previewedPaise || created.amount.currency !== previewedCurrency);

    if (priceChanged) {
      priceChangeConfirmedRef.current = false;
      setPriceChangeNotice({
        previewedPaise: previewedPaise!,
        previewedCurrency: previewedCurrency!,
        frozenPaise: created.amount.totalPaise,
        frozenCurrency: created.amount.currency,
      });
      return;
    }

    // Pass `created` explicitly — chaining straight from createPurchaseStep's
    // resolved value in this same handler, `purchaseFlow.purchase` state has
    // not re-rendered into this closure yet (see useCreatePurchase.ts's note
    // on proceedToCheckout's signature).
    await purchaseFlow.proceedToCheckout(created);
  }

  return (
    <div className="mx-auto max-w-4xl px-5 py-16 sm:px-8">
      <h1 className="text-3xl font-bold text-on-surface mb-8">Checkout</h1>

      {loading && (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="aspect-[3/4] animate-pulse rounded-[2rem] bg-surface border border-outline-variant/30" />
          <div className="space-y-4">
            <div className="h-6 w-2/3 animate-pulse rounded-lg bg-surface" />
            <div className="h-40 animate-pulse rounded-[1.75rem] bg-surface" />
          </div>
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center gap-4 rounded-[2rem] border border-outline-variant/30 bg-surface p-12 text-center">
          <p className="text-sm font-medium text-status-failed">{COLLECTION_LOAD_FAILED_MESSAGE}</p>
          <button type="button" onClick={() => void reload()} className="text-sm font-semibold text-primary hover:underline">
            Retry
          </button>
        </div>
      )}

      {!loading && !error && collection && (
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <div className="flex flex-col gap-4">
            <div className="aspect-[3/4] overflow-hidden rounded-[2rem] border border-outline-variant/30 bg-surface-low">
              <TemplateCardPreview template={collection.template} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-on-surface">{collection.displayName}</h2>
              {collection.description && (
                <p className="mt-1 text-sm text-on-surface-variant">{collection.description}</p>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {buyCreditIntent && (
              <div className="rounded-2xl bg-primary/5 border border-primary/20 px-5 py-3 text-sm text-on-surface-variant">
                You’re buying a Publish Credit for your draft invitation. You’ll return to publish it once payment is
                confirmed.
              </div>
            )}
            <PriceBreakdown
              quote={pricing.quote}
              loading={pricing.loading}
              recalculating={pricing.recalculating}
              error={pricing.error}
              productUnavailable={pricing.productUnavailable}
              onRetry={() => void pricing.retry()}
            />

            {!isPartner && !pricing.loading && !pricing.productUnavailable && (
              <CouponInput
                appliedCouponCode={pricing.appliedCouponCode}
                couponError={pricing.couponError}
                // Locked the instant a Purchase exists (any phase from
                // `creating-purchase` through `error`/`ready-for-payment`),
                // not just while a request is in flight — the Purchase froze
                // the price server-side the moment it was created, so
                // changing the coupon here would update this page's preview
                // without ever re-freezing the Purchase that's actually
                // about to be charged (caught in code review: applying a
                // coupon while a Purchase already existed silently detached
                // the displayed total from the amount `Retry Payment` would
                // go on to charge).
                //
                // Also locked on `phase === 'error'` even with no Purchase
                // yet (a failed `createPurchase` call itself) — a retry click
                // reuses the SAME `Idempotency-Key`, which per
                // `05_API_SPECIFICATION.md §4` replays the *original*
                // request's outcome for 24h; changing the coupon first and
                // then retrying with that same key would be a different
                // logical request wearing an old request's idempotency token
                // (caught in code review).
                busy={
                  pricing.recalculating ||
                  isBusy ||
                  purchaseFlow.purchase !== null ||
                  purchaseFlow.phase === 'error'
                }
                onApply={pricing.applyCoupon}
                onRemove={pricing.removeCoupon}
              />
            )}

            {purchaseFlow.error && (
              <div role="alert" className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-4 text-status-failed font-medium text-sm">
                {purchaseFlow.error}
              </div>
            )}

            {purchaseFlow.phase === 'paid' ? (
              <div className="rounded-2xl bg-status-published/10 border border-status-published/30 px-6 py-5 flex flex-col gap-2 text-sm">
                <p className="flex items-center gap-2 font-semibold text-on-surface">
                  <Icons.CheckCircle size={18} className="text-status-published" />
                  Payment successful
                </p>
                <p className="text-on-surface-variant">
                  {purchaseFlow.purchase?.orderNumber ? (
                    <>
                      Order <span className="font-semibold text-on-surface font-mono">{purchaseFlow.purchase.orderNumber}</span> is confirmed. We’ll take it from here.
                    </>
                  ) : (
                    'Your order is confirmed. We’ll take it from here.'
                  )}
                </p>
                {buyCreditIntent && (
                  <Button
                    variant="primary"
                    size="md"
                    className="mt-2 self-start"
                    onClick={() => {
                      clearBuyCreditIntent();
                      navigate(buyCreditIntent.returnTo);
                    }}
                  >
                    Continue to publish
                  </Button>
                )}
              </div>
            ) : purchaseFlow.phase === 'confirmation-pending' ? (
              <div className="rounded-2xl bg-surface border border-outline-variant/30 px-6 py-5 flex flex-col gap-3 text-sm">
                <p className="font-semibold text-on-surface">We’re still confirming your payment</p>
                <p className="text-on-surface-variant">
                  This can take a minute. We’ll email you as soon as it’s done — you can also check again now.
                </p>
                <Button variant="secondary" size="md" onClick={() => void purchaseFlow.refreshStatus()}>
                  Check status
                </Button>
              </div>
            ) : purchaseFlow.phase === 'awaiting-gateway' || purchaseFlow.phase === 'confirming' ? (
              <div className="rounded-2xl bg-primary/5 border border-primary/20 px-6 py-5 flex items-center gap-3 text-sm">
                <Icons.Loader size={18} className="animate-spin text-primary" />
                <p className="font-semibold text-on-surface">
                  {purchaseFlow.phase === 'awaiting-gateway' ? 'Opening secure payment…' : 'Confirming your payment…'}
                </p>
              </div>
            ) : purchaseFlow.purchase?.status === 'CANCELLED' ? (
              <div className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-5 flex flex-col gap-2 text-sm">
                <p className="flex items-center gap-2 font-semibold text-on-surface">
                  <Icons.Warning size={18} className="text-status-failed" />
                  Order cancelled
                </p>
                <p className="text-on-surface-variant">
                  {purchaseFlow.purchase.orderNumber ? (
                    <>Order <span className="font-semibold text-on-surface font-mono">{purchaseFlow.purchase.orderNumber}</span> was cancelled.</>
                  ) : (
                    'This order was cancelled.'
                  )}{' '}
                  If a payment was completed, it has been automatically refunded to your original payment method.
                </p>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={purchaseFlow.reset}
                  className="mt-2 self-start"
                >
                  Start new order
                </Button>
              </div>
            ) : purchaseFlow.purchase?.status === 'EXPIRED' ? (
              <div className="rounded-2xl bg-status-failed/10 border border-status-failed/30 px-6 py-5 flex flex-col gap-2 text-sm">
                <p className="flex items-center gap-2 font-semibold text-on-surface">
                  <Icons.Warning size={18} className="text-status-failed" />
                  Order expired
                </p>
                <p className="text-on-surface-variant">
                  {purchaseFlow.purchase.hasRefundedPayment || (purchaseFlow.purchase.refundedAmountPaise && purchaseFlow.purchase.refundedAmountPaise > 0) ? (
                    <>
                      {purchaseFlow.purchase.orderNumber ? (
                        <>Order <span className="font-semibold text-on-surface font-mono">{purchaseFlow.purchase.orderNumber}</span> expired. </>
                      ) : (
                        'This order expired. '
                      )}
                      Your payment of{' '}
                      <span className="font-semibold text-on-surface">
                        {formatPrice(purchaseFlow.purchase.refundedAmountPaise ?? purchaseFlow.purchase.amount.totalPaise, purchaseFlow.purchase.amount.currency, { exact: true })}
                      </span>{' '}
                      was automatically refunded to your bank.
                    </>
                  ) : purchaseFlow.purchase.walletCredit ? (
                    <>
                      {purchaseFlow.purchase.orderNumber ? (
                        <>Order <span className="font-semibold text-on-surface font-mono">{purchaseFlow.purchase.orderNumber}</span> expired. </>
                      ) : (
                        'This order expired. '
                      )}
                      Your payment was added to your wallet ({purchaseFlow.purchase.walletCredit.credits.toLocaleString()} credits).
                    </>
                  ) : (
                    <>
                      {purchaseFlow.purchase.orderNumber ? (
                        <>Order <span className="font-semibold text-on-surface font-mono">{purchaseFlow.purchase.orderNumber}</span> has expired.</>
                      ) : (
                        'This order has expired.'
                      )}{' '}
                      If a payment was received after expiry, it has been automatically refunded.
                    </>
                  )}
                </p>
                <Button
                  variant="secondary"
                  size="md"
                  onClick={purchaseFlow.reset}
                  className="mt-2 self-start"
                >
                  Start new order
                </Button>
              </div>
            ) : (isReadyForPayment || purchaseFlow.phase === 'payment-failed') && purchaseFlow.checkout ? (
              <div className="flex flex-col gap-3">
                <div className={`rounded-2xl border px-6 py-5 flex flex-col gap-2 text-sm ${purchaseFlow.phase === 'payment-failed' ? 'bg-status-failed/5 border-status-failed/20' : 'bg-primary/5 border-primary/20'}`}>
                  <p className="flex items-center gap-2 font-semibold text-on-surface">
                    {purchaseFlow.phase === 'payment-failed' ? (
                      <>
                        <Icons.Warning size={18} className="text-status-failed" />
                        {purchaseFlow.error === PAYMENT_UNAVAILABLE_MESSAGE ? 'Couldn’t open payment' : 'Payment unsuccessful'}
                      </>
                    ) : (
                      <>
                        <Icons.CheckCircle size={18} className="text-status-published" />
                        Order created — ready for payment
                      </>
                    )}
                  </p>
                  <p className="text-on-surface-variant">
                    {purchaseFlow.phase === 'payment-failed' ? (
                      <>{purchaseFlow.error === PAYMENT_UNAVAILABLE_MESSAGE ? 'The payment window could not be opened.' : 'Your payment was not completed.'} You can try again below.</>
                    ) : (
                      <>
                        You’ll be charged{' '}
                        <span className="font-bold text-on-surface">
                          {formatPrice(purchaseFlow.checkout.checkout.amountPaise, purchaseFlow.checkout.checkout.currency, { exact: true })}
                        </span>{' '}
                        via Razorpay.
                      </>
                    )}
                  </p>
                </div>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={() => void purchaseFlow.openPayment()}
                  className="w-full"
                >
                  {purchaseFlow.phase === 'payment-failed' ? 'Try Again' : 'Pay Now'}
                </Button>
              </div>
            ) : (
              <Button
                variant="primary"
                size="lg"
                onClick={() => void handleConfirmAndPay()}
                loading={isBusy}
                disabled={isBusy || pricing.loading || pricing.recalculating || !pricing.quote || pricing.productUnavailable}
                className="w-full"
              >
                {purchaseFlow.phase === 'creating-purchase'
                  ? 'Creating your order…'
                  : purchaseFlow.phase === 'creating-checkout'
                    ? 'Preparing payment…'
                    : purchaseFlow.phase === 'cancelling'
                      ? 'Cancelling…'
                      : purchaseFlow.purchase && purchaseFlow.phase === 'error'
                        ? 'Retry Payment'
                        : 'Confirm & Pay'}
              </Button>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={priceChangeNotice !== null}
        onClose={() => {
          if (!priceChangeConfirmedRef.current) {
            // A real cancel — the user backed out without confirming. Cancel
            // the Purchase the price-change check already created, rather
            // than leaving it dangling (it would otherwise only clean up via
            // the backend's own 30-minute expiry sweep).
            void purchaseFlow.cancel('price_changed_declined');
            // The cancelled Purchase is now a terminal Purchase
            // (`01_PURCHASE_DOMAIN.md §11`: "a new Purchase is always created
            // if the existing Purchase ... has reached a terminal state") —
            // a later Buy Now click starts a genuinely new logical attempt,
            // so it needs a genuinely new idempotency key, not the one that
            // already identified the now-cancelled attempt.
            idempotencyKeyRef.current = mintIdempotencyKey();
          }
          setPriceChangeNotice(null);
          priceChangeConfirmedRef.current = false;
        }}
        onConfirm={async () => {
          priceChangeConfirmedRef.current = true;
          await purchaseFlow.proceedToCheckout();
        }}
        title="The price has changed"
        description={
          priceChangeNotice
            ? `We showed you ${formatPrice(priceChangeNotice.previewedPaise, priceChangeNotice.previewedCurrency, { exact: true })}, but your order was created at ${formatPrice(priceChangeNotice.frozenPaise, priceChangeNotice.frozenCurrency, { exact: true })}. This is the amount you'll be charged.`
            : undefined
        }
        confirmLabel="Continue to Payment"
        cancelLabel="Cancel"
        tone="primary"
      />
    </div>
  );
}

export default CheckoutPage;
